import {
    DataType,
    IDrawingShapeData,
    ILayoutData,
    IPageDataModel,
    IPresentationDataModel,
    IShapeData,
    ISlideData,
    PresentationDataModel,
    STShapeType,
} from '@tencent/slide-sdk-model';
import {
    ICopiedLayoutData,
    ICopiedSlideData,
    IReorganizeParams,
    CopiedKeyType,
    ISdkData,
    IClipboardData,
    ISlideClipboardData,
} from '@src/slide/domain/ui-model/element-modeles/property/copy/copy-model/paste-special.interface';
import cloneDeep from 'lodash/cloneDeep';
import { IResourceMapData } from '@tencent/slide-sdk-model/src/data/resource-map-data';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import unset from 'lodash/unset';
import set from 'lodash/set';
import { prstGeomGdNames } from '@src/slide/domain/ui-model/converter/node-property/module-property/graphic-info/graphic-info-converter';
import { IMAGE_ATTACHMENT_PASTE_PLACEHOLDER } from '@src/slide/domain/ui-model/element-modeles/property/attachment/constants';

/**
 * 选择性粘贴的工具类
 */
export default class PasteSpecialUtils {
    /**
     * 重新组织剪贴板中的幻灯片数据
     * 更新已复制幻灯片的layoutId以及删除noteSlideId
     * @param param0 IReorganizeParams
     * @returns ICopiedSlideData[]
     */
    public static reorganizeSlideSdkData({
        clipboardData,
        insertSlideIndex,
        sdkDataV3,
        slideSdkDataModify,
        uiDataService,
    }: IReorganizeParams): ICopiedSlideData[] | undefined {
        const dataArray = clipboardData.data as ICopiedSlideData[];
        const diffDoc = clipboardData.globalPadId !== SlideDataServices.DocInfoManager()?.globalPadId;
        const { slideSize, layoutList: copiedLayoutSdkDataList } = clipboardData as ISlideClipboardData;
        // 控制点脏数据兜底
        dataArray.forEach((sdkData) => {
            this.modifyPrstGeomAdjName(sdkData.ownSdkData?.cSld?.spTree?.shapeList);
            this.modifyPrstGeomAdjName(sdkData.inheritedSdkData?.cSld?.spTree?.shapeList);
            this.addPrstGeomInPicture(sdkData.ownSdkData?.cSld?.spTree?.shapeList);
            this.addPrstGeomInPicture(sdkData.inheritedSdkData?.cSld?.spTree?.shapeList);
        });
        const newSlideSdkDataList = cloneDeep(dataArray);
        newSlideSdkDataList.forEach((slideAllSdkData: ICopiedSlideData) => {
            // 当复制的数据里存在 layoutList 的时候，优先通过匹配关系找到 layoutId（getDestinationLayoutId）
            // 否则直接分配一个已有的 layoutId（slideSdkDataModify.adjustPageData）
            const targetLayoutId = copiedLayoutSdkDataList && this.getDestinationLayoutId(
                insertSlideIndex,
                sdkDataV3,
                slideAllSdkData.ownSdkData,
                copiedLayoutSdkDataList,
            );
            Object.keys(slideAllSdkData).forEach((key: string) => {
                const slideSdkData = slideAllSdkData[key] as ISlideData;
                if (diffDoc && clipboardData.slideSize) {
                    const isChangeLayout = !!targetLayoutId;
                    slideSdkDataModify.adjustPageData(slideSdkData, slideSize, isChangeLayout);
                }
                // 使用目标主题粘贴时（默认），应用ownSdkData，需要修改layoutId
                if (key === CopiedKeyType.OWN && targetLayoutId) {
                    slideSdkData.layoutId = targetLayoutId;
                }
                // 原有逻辑：需要手动移除旧的 noteSlideId，否则会导致粘贴的备注页无法成功创建
                Reflect.deleteProperty(slideSdkData, 'noteSlideId');
            });
        });

        return newSlideSdkDataList;
    }

    /**
     * 更新文档的资源数据（图片、音频。视频、字体）
     * @param presentation PresentationDataModel
     * @param resourceMap IResourceMapData
     */
    public static updateResourceMap({
        presentation,
        resourceMap,
        copiedDocId,
    }: {
        presentation: PresentationDataModel;
        resourceMap: IResourceMapData;
        copiedDocId: string;
    }) {
        const hasMedia = Object.keys(resourceMap.audios).length || Object.keys(resourceMap.videos).length;
        if (hasMedia && copiedDocId !== SlideDataServices.DocInfoManager()?.globalPadId) {
            resourceMap.audios = {};
            resourceMap.videos = {};
        }
        presentation.api.updateResourceMap(resourceMap);
    }

    /**
     * 跨文档粘贴时，移除剪贴板中的附件数据
     * @param clipboardData 剪贴板数据
     * @returns 是否有附件被删除
     */
    public static removeAttachment(clipboardData: IClipboardData): boolean {
        if (clipboardData.globalPadId === SlideDataServices.DocInfoManager()?.globalPadId) return false;
        const ownSdkDataList = clipboardData.data.map(({ ownSdkData }) => ownSdkData);
        const inheritedSdkDataList = clipboardData.data.map(({ inheritedSdkData }) => inheritedSdkData);
        return [ownSdkDataList, inheritedSdkDataList].reduce(
            (result, dataList) => this.removeMediaProperty(dataList) || result,
            false,
        );
    }

    /**
     * 获取目标主题的占位符继承数据
     * @param shapeData 元素sdk自身数据
     * @param slideDataModel 目标页slide
     * @returns 应用目标主题占位符数据
     */
    public static updatePlaceHolderData(shapeData: IShapeData, slideDataModel: IPageDataModel): ISdkData {
        if (!shapeData?.nvPr?.ph) return shapeData;
        // 获取使用目标主题的placeHolder的继承样式
        const appliedDestPhData = SlideDataServices.SDKDataServices.DataHelper()?.getInheritedDataFromPlaceholder(
            shapeData,
            slideDataModel,
        );
        if (!appliedDestPhData) {
            if (slideDataModel.type !== DataType.SlideLayout) unset(shapeData?.nvPr, 'ph');
            return shapeData;
        }
        if (slideDataModel.type !== DataType.SlideLayout) unset(appliedDestPhData?.nvPr, 'ph');
        return appliedDestPhData as ISdkData;
    }

    public static modifyPrstGeomAdjName(dataArray: IDrawingShapeData[] | undefined) {
        const shapeArr = (dataArray?.filter(e => (e?.type === DataType.Shape || e?.type === DataType.Picture)
            && e.spPr?.prstGeom?.avLst?.gd) ?? []) as IShapeData[];
        shapeArr.forEach((shape) => {
            const { gd = [] } = shape.spPr?.prstGeom?.avLst ?? {};
            if (!shape.spPr?.prstGeom?.prst) {
                return;
            }
            const prstType = STShapeType[shape.spPr.prstGeom.prst];
            gd.forEach((point, index) => {
                if (Object.keys(prstGeomGdNames).includes(prstType)) {
                    const names = prstGeomGdNames[prstType];
                    if (index <= names.length) {
                        point.name = names[index];
                        return;
                    }
                }
                if (gd.length === 1 && !prstType.includes('Connector')) {
                    point.name = 'adj';
                    return;
                }
                point.name = `adj${index + 1}`;
            });
        });
    }

    public static addPrstGeomInPicture(dataArray: IDrawingShapeData[] | undefined) {
        // 如果不是预设图形且不是自定义图形，补充预设图形属性，不然图片没有承载的图形无法显示
        const shapeArr = dataArray?.filter((e) => {
            if (e?.type === DataType.Picture) {
                const hasPrstGeom = e.spPr?.prstGeom?.prst;
                const hasCustPath = e.spPr?.custGeom?.pathLst?.path?.some(p => !!p.pathString || !!p.egPath2D);
                return !hasPrstGeom && !hasCustPath;
            }
            return false;
        }) ?? [];
        (shapeArr as IShapeData[]).forEach((shape) => {
            shape.spPr && (shape.spPr.prstGeom = { prst: STShapeType.rect });
            shape.spPr && (shape.spPr.custGeom = undefined);
        });
    }

    /**
     * 获取目标版式layoutId
     * 有同名版式则为目标版式，无同名版式则需在目标母版中创建新版式作为目标版式
     * @param insertSlideIndex 幻灯片插入位置
     * @param sdkDataV3 当前文档的PresentationDataModel
     * @param slideSdkData ISlideData已复制的幻灯片数据
     * @param copiedLayoutSdkDataList 已复制的版式数据
     * @returns 目标版式layoutId
     */
    private static getDestinationLayoutId(
        insertSlideIndex: number,
        sdkDataV3: IPresentationDataModel,
        slideSdkData: ISlideData,
        copiedLayoutSdkDataList: ICopiedLayoutData[],
    ): string | undefined {
        // 获取已复制slide的版式的name
        const layoutOwnSdkData = this.getCopiedLayoutData(slideSdkData, copiedLayoutSdkDataList);
        if (!layoutOwnSdkData) return;
        const copiedLayoutName = layoutOwnSdkData.cSld?.name;

        // 找到目标主题所在的母版Id, 以及该母版下的所有版式
        const targetMaster = sdkDataV3?.slideList?.[insertSlideIndex]?.getMaster();
        if (!targetMaster) return;
        const targetLayoutList = targetMaster.layoutList;
        // 从目标版式列表中，找到相同layoutName的版式，并返回
        const targetLayoutModel = targetLayoutList?.find((layoutDataModel) => {
            // @ts-ignore
            const name = layoutDataModel.props.data.cSld?.name;
            // 应该用layoutType去匹配，但目前由于导入导出的数据中缺少layoutType，暂时先用name进行过滤
            return name === copiedLayoutName;
        });

        if (targetLayoutModel) {
            return targetLayoutModel.id;
        }

        // 若找不到相同layoutName的版式，则在该文档的目标母版中添加新的版式，并将返回新版式的id
        // 新创建的版式，插入位置在最后
        const [idsMap] = sdkDataV3.api.addLayouts(
            [{ ...layoutOwnSdkData, masterId: targetMaster.id }],
            targetMaster.layoutList.length,
        );

        return idsMap.get(layoutOwnSdkData.id);
    }

    /**
     * 获取已复制幻灯片的版式数据
     * @returns 版式数据 ILayoutData
     */
    private static getCopiedLayoutData(
        slideSdkData: ISlideData,
        copiedLayoutSdkDataList: ICopiedLayoutData[],
    ): ILayoutData | undefined {
        const { layoutId } = slideSdkData;
        if (!layoutId) return;
        const layoutData = copiedLayoutSdkDataList.find((copiedLayoutData) => {
            const id = copiedLayoutData?.ownSdkData?.id;
            return id === layoutId;
        });
        return layoutData?.ownSdkData;
    }

    /**
     * 移除图片下的音视频
     * @param dataList ISdkData[]
     */
    private static removeMediaProperty(dataList: ISdkData[]): boolean {
        if (!dataList?.length) return false;
        return dataList.reduce((result, data) => {
            if (data.type === DataType.Picture) {
                // 图片
                if (data.nvPr?.egMedia) {
                    // 音视频附件
                    unset(data.nvPr, 'egMedia');
                    unset(data.nvPr, 'hlinkClick');
                    return true;
                }

                if (data.nvPr?.attachment) {
                    // 图片附件
                    unset(data.nvPr, 'attachment');
                    // 目前图片附件落盘的地址都是一个透明图片，为了避免粘贴过去用户觉得奇怪，使用一个默认占位图替换
                    set(data, 'blipFill.blip.embed', IMAGE_ATTACHMENT_PASTE_PLACEHOLDER);
                    set(data, 'blipFill.srcRect', { b: 0, l: 0, t: 0, r: 0 });
                    return true;
                }
            }

            if (data.type === DataType.Shape) {
                // 文字链附件
                if (!data.nvPr?.attachment) return false || result;
                unset(data.nvPr, 'attachment');
                return true;
            }

            if (data.type === DataType.Slide) {
                // 整页幻灯片
                const shapeDataList = data.cSld?.spTree?.shapeList as ISdkData[];
                return this.removeMediaProperty(shapeDataList);
            }

            if (data.type === DataType.GroupShape) {
                // 组合
                const shapeDataList = data?.shapeList;
                return this.removeMediaProperty(shapeDataList);
            }
            return false || result;
        }, false);
    }
}
